import { useEffect, useState } from 'react';

/**
 * Returns a debounced copy of `value` that only updates after `value` has
 * stopped changing for `delay` milliseconds.
 *
 * Useful for deferring expensive work (search requests, URL writes) until the
 * user pauses typing. The timer resets on every change, so only the latest
 * value settles.
 *
 * @param value - The rapidly-changing source value.
 * @param delay - Quiet period in milliseconds before the value settles (default 300).
 */
export function useDebouncedValue<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);

  return debounced;
}
